import React, { useEffect } from "react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import {
	CourierDelivery,
	DeliveryStatusEnum,
	DELIVERY_STATUS_LABELS,
} from "@/lib/types/delivery.types";
import { DeliveryStatusBadge } from "../job-page/job-page-components/deliveries/delivery-status-badge.component";
import { useNavigate } from "react-router-dom";
import { LogOut, Package } from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import { getProfileDisplayName } from "@/lib/utils/client-utils";

export const CourierDashboardPage: React.FC = () => {
	const navigate = useNavigate();
	const {
		courierDeliveries,
		courierDeliveriesLoading,
		fetchCourierDeliveries,
	} = useJobStore((state) => ({
		courierDeliveries: state.courierDeliveries,
		courierDeliveriesLoading: state.courierDeliveriesLoading,
		fetchCourierDeliveries: state.fetchCourierDeliveries,
	}));
	const { profile, organization, handleSignOut } = useCentralStore(
		(state) => ({
			profile: state.profile,
			organization: state.organization,
			handleSignOut: state.handleSignOut,
		})
	);

	useEffect(() => {
		fetchCourierDeliveries();
	}, []);

	const formatDate = (dateStr: string | null) => {
		if (!dateStr) return null;
		return new Date(dateStr).toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const groupedDeliveries = {
		active: courierDeliveries.filter(
			(d) =>
				d.status === DeliveryStatusEnum.IN_TRANSIT ||
				d.status === DeliveryStatusEnum.PENDING
		),
		completed: courierDeliveries.filter(
			(d) =>
				d.status === DeliveryStatusEnum.DELIVERED ||
				d.status === DeliveryStatusEnum.FAILED
		),
	};

	return (
		<div
			className="min-h-screen bg-gray-50"
			data-testid="courier-dashboard"
		>
			<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
				<div>
					<h1 className="text-lg font-semibold text-gray-900">
						{organization?.name || "dentLAB"}
					</h1>
					<p className="text-sm text-gray-500">
						{getProfileDisplayName(profile)}
					</p>
				</div>
				<Button variant="ghost" size="sm" onClick={handleSignOut}>
					<LogOut className="h-4 w-4" />
				</Button>
			</header>

			<main className="max-w-lg mx-auto px-4 py-4 space-y-4">
				{courierDeliveriesLoading ? (
					<div className="text-center py-8 text-gray-500">
						Laden...
					</div>
				) : courierDeliveries.length === 0 ? (
					<div className="text-center py-12 space-y-2">
						<Package className="h-12 w-12 text-gray-300 mx-auto" />
						<p className="text-gray-500">
							Keine Lieferungen zugewiesen
						</p>
					</div>
				) : (
					<>
						{groupedDeliveries.active.length > 0 && (
							<Section title="Aktive Lieferungen">
								{groupedDeliveries.active.map((delivery) => (
									<CourierDeliveryCard
										key={delivery.id}
										delivery={delivery}
										formatDate={formatDate}
										onClick={() =>
											navigate(
												`/lieferung/${delivery.id}`
											)
										}
									/>
								))}
							</Section>
						)}
						{groupedDeliveries.completed.length > 0 && (
							<Section
								title="Abgeschlossen"
								data-testid="courier-history-section"
							>
								{groupedDeliveries.completed.map((delivery) => (
									<CourierDeliveryCard
										key={delivery.id}
										delivery={delivery}
										formatDate={formatDate}
										onClick={() =>
											navigate(
												`/lieferung/${delivery.id}`
											)
										}
									/>
								))}
							</Section>
						)}
					</>
				)}
			</main>
		</div>
	);
};

const Section: React.FC<{
	title: string;
	children: React.ReactNode;
	"data-testid"?: string;
}> = ({ title, children, ...props }) => (
	<div {...props}>
		<h2 className="text-sm font-medium text-gray-500 mb-2">{title}</h2>
		<div className="space-y-2">{children}</div>
	</div>
);

const CourierDeliveryCard: React.FC<{
	delivery: CourierDelivery;
	formatDate: (d: string | null) => string | null;
	onClick: () => void;
}> = ({ delivery, formatDate, onClick }) => (
	<div
		className="bg-white rounded-lg border border-gray-200 p-3 space-y-1.5 cursor-pointer hover:border-gray-300 active:bg-gray-50 transition-colors"
		onClick={onClick}
		data-testid={`courier-delivery-card-${delivery.id}`}
	>
		<div className="flex items-center justify-between">
			<DeliveryStatusBadge status={delivery.status} />
			<span className="text-xs text-gray-400 font-mono">
				{delivery.job_code}
			</span>
		</div>
		{delivery.client_name && (
			<p className="text-sm font-medium text-gray-900">
				{delivery.client_name}
			</p>
		)}
		{delivery.description && (
			<p className="text-sm text-gray-600 line-clamp-1">
				{delivery.description}
			</p>
		)}
		{delivery.scheduled_departure_at && (
			<p className="text-xs text-gray-500">
				Geplant: {formatDate(delivery.scheduled_departure_at)}
			</p>
		)}
	</div>
);
